import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Box, CircularProgress, Typography } from '@mui/material';
import { CheckCircle } from '@mui/icons-material';
import { useSearchParams } from 'react-router';
import Modal from './modal';
import { useTranslations } from '@/i18n/use-translations.tsx';
import { useGetEventOrdersQuery, GET_EVENT_ORDERS_QUERY_KEY } from '@/state/queries/orders/use-get-event-orders.ts';
import { GET_PRODUCTS_QUERY_KEY } from '@/state/queries/orders/use-get-products';
import { GET_EVENT_QUERY_KEY } from '@/state/queries/events/use-get-event';
import { invalidateAllQuotaBalances } from '@/state/queries/entitlements/use-quota-balances';
import { ORDER_STATUS, type OrderStatus } from '@/types/dtos/orders/response/order.dto.ts';
import { useQueryClient } from '@tanstack/react-query';

interface PaymentProcessingModalProps {
  eventId?: string;
}

export default function PaymentProcessingModal({ eventId }: PaymentProcessingModalProps) {
  const t = useTranslations();
  const [searchParams, setSearchParams] = useSearchParams();
  const queryClient = useQueryClient();
  const intervalRef = useRef<number | undefined>(undefined);
  const modalStartTimeRef = useRef<number | undefined>(undefined);
  const closeTimeoutRef = useRef<number | undefined>(undefined);
  const previousOrderStatusesRef = useRef<Record<string, OrderStatus>>({});
  const [showSuccess, setShowSuccess] = useState(false);

  const handlePaymentSuccess = useCallback(() => {
    setShowSuccess(true);

    // Clear any existing close timeout
    if (closeTimeoutRef.current) {
      window.clearTimeout(closeTimeoutRef.current);
    }

    // Check if minimum 3 seconds have passed
    const elapsedTime = Date.now() - modalStartTimeRef.current!;
    const remainingTime = Math.max(0, 3000 - elapsedTime);

    // Close modal after remaining time, then wait a bit more before invalidating queries
    // to give backend time to process the payment completion
    closeTimeoutRef.current = window.setTimeout(() => {
      const newSearchParams = new URLSearchParams(searchParams);
      newSearchParams.delete('paymentProcessing');
      setSearchParams(newSearchParams);

      // Wait 2 more seconds to give backend time to process payment completion
      setTimeout(() => {
        // Invalidate orders and related queries to refresh the UI with purchased items
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_ORDERS_QUERY_KEY, eventId] });
        queryClient.invalidateQueries({ queryKey: [GET_PRODUCTS_QUERY_KEY] });
        queryClient.invalidateQueries({ queryKey: [GET_EVENT_QUERY_KEY, eventId] });
        void invalidateAllQuotaBalances(queryClient);

        // Also refetch paid features to ensure store is updated
        queryClient.refetchQueries({ queryKey: [GET_PRODUCTS_QUERY_KEY] });
        queryClient.refetchQueries({ queryKey: [GET_EVENT_QUERY_KEY, eventId] });
      }, 2000);
    }, remainingTime);
  }, [searchParams, setSearchParams, queryClient, eventId]);

  const paymentProcessing = searchParams.get('paymentProcessing') === 'true';

  const { data: orders } = useGetEventOrdersQuery(eventId || undefined);

  // Show modal and record start time when paymentProcessing is true and we have eventId
  useEffect(() => {
    if (paymentProcessing && eventId && !modalStartTimeRef.current) {
      modalStartTimeRef.current = Date.now();
      // Use queueMicrotask to defer state update
      queueMicrotask(() => setShowSuccess(false));
    } else if (!paymentProcessing || !eventId) {
      modalStartTimeRef.current = undefined;
      // Clear any pending close timeout
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
        closeTimeoutRef.current = undefined;
      }
    }
  }, [paymentProcessing, eventId]);

  const isPaymentPending = useMemo(() => {
    if (!orders || orders.length === 0) return false;
    return orders.some((o) => o.status === ORDER_STATUS.PAYMENT_PENDING);
  }, [orders]);

  const hasPaidOrders = useMemo(() => {
    if (!orders || orders.length === 0) return false;
    return orders.some((o) => o.status === ORDER_STATUS.PAID);
  }, [orders]);

  // Check if payment is already completed and close modal after minimum time
  useEffect(() => {
    if (paymentProcessing && eventId && modalStartTimeRef.current && hasPaidOrders && !isPaymentPending) {
      // Use queueMicrotask to defer the callback
      queueMicrotask(() => handlePaymentSuccess());
    }
  }, [paymentProcessing, eventId, hasPaidOrders, isPaymentPending, handlePaymentSuccess]);

  // Detect transition from PAYMENT_PENDING to PAID to show success and close modal
  useEffect(() => {
    if (!orders || !paymentProcessing || !modalStartTimeRef.current) return;
    let transitionedToPaid = false;
    const currentStatuses: Record<string, OrderStatus> = {};
    for (const order of orders) {
      currentStatuses[order.id] = order.status;
      const previousStatus = previousOrderStatusesRef.current[order.id];
      if (previousStatus === ORDER_STATUS.PAYMENT_PENDING && order.status === ORDER_STATUS.PAID) {
        transitionedToPaid = true;
      }
    }
    previousOrderStatusesRef.current = currentStatuses;
    if (transitionedToPaid) {
      // Use queueMicrotask to defer the callback
      queueMicrotask(() => handlePaymentSuccess());
    }
  }, [orders, paymentProcessing, eventId, handlePaymentSuccess]);

  // Poll for order status changes when payment is pending and processing is active
  useEffect(() => {
    if (!eventId || !isPaymentPending || !paymentProcessing) {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
      return;
    }

    // Start polling
    intervalRef.current = window.setInterval(() => {
      queryClient.invalidateQueries({ queryKey: [GET_EVENT_ORDERS_QUERY_KEY, eventId] });
    }, 3000);

    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
        intervalRef.current = undefined;
      }
    };
  }, [eventId, isPaymentPending, paymentProcessing, queryClient]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        window.clearInterval(intervalRef.current);
      }
      if (closeTimeoutRef.current) {
        window.clearTimeout(closeTimeoutRef.current);
      }
    };
  }, []);

  const handleClose = () => {
    const newSearchParams = new URLSearchParams(searchParams);
    newSearchParams.delete('paymentProcessing');
    setSearchParams(newSearchParams);
  };

  if (!paymentProcessing || !eventId) return null;

  return (
    <Modal
      title={t('payment.processing.title')}
      description={t('payment.processing.description')}
      open={paymentProcessing && !!eventId}
      handleClose={handleClose}
      hideCloseIcon={true} // Don't allow manual close during processing
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          py: 4,
          gap: 2,
        }}
      >
        {showSuccess ? (
          <>
            <CheckCircle sx={{ fontSize: 64, color: 'success.main' }} />
            <Typography variant="h6" color="success.main">
              {t('payment.processing.success')}
            </Typography>
          </>
        ) : (
          <>
            <CircularProgress size={64} />
            <Typography variant="h6">{t('payment.processing.waiting')}</Typography>
            <Typography variant="body2" color="text.secondary">
              {t('payment.processing.doNotClose')}
            </Typography>
          </>
        )}
      </Box>
    </Modal>
  );
}
